import infoIcon from "@assets/images/info.svg";
import RefreshIcon from "@assets/images/refresh-primary.svg";
import Search from "@assets/images/Search-new.svg";
import warningRed from "@assets/images/warning-red.svg";
import { Box, Button, Grid, Typography } from "@material-ui/core";
import Autocomplete from "@saleor/components/Autocomplete";
import MyTypography from "@saleor/components/MyTypography";
import SingleSelectField, {
  Choice
} from "@saleor/components/SingleSelectField";
import { SingleTextField } from "@saleor/components/SingleTextField";
import {
  CustomerMaterialsQuery,
  SearchMaterialBy,
  useCustomerMaterialsLazyQuery,
  useGetSaleOrgAndDistChannelBySoldToQuery,
  useSearchSuggestionMaterialLazyQuery,
  useSearchSuggestionSoldTosCustMatLazyQuery
} from "@saleor/graphql";
import useNotifier from "@saleor/hooks/useNotifier";
import { mapEdgesToItems } from "@saleor/utils/maps";
import { FormikProps } from "formik";
import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState
} from "react";
import InlineSVG from "react-inlinesvg";
import { FormattedMessage, useIntl } from "react-intl";

import DialogWaitingConfirm from "../DialogWaitingConfirm";
import useStyles from "./styles";

interface EditMaterial {
  formik: FormikProps<any>;
  onReset?: () => void;
  resetFilter?: boolean;
  setDownloadDisable: Dispatch<SetStateAction<any>>;
  handleSearchData?: (data: CustomerMaterialsQuery) => void;
}

const EditMaterial: React.FC<EditMaterial> = ({
  formik,
  onReset,
  resetFilter,
  setDownloadDisable,
  handleSearchData
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const [reset, setReset] = useState<boolean>(true);
  const [salesOrganizationOptions, setSalesOrganizationOptions] = useState<
    Choice[]
  >([]);
  const [distributionChannelOptions, setDistributionChannelOptions] = useState<
    Choice[]
  >([]);
  const [elementEnableProperty, setElementEnableProperty] = useState<boolean>(
    false
  );
  const [resetButtonEnableProperty, setResetButtonEnableProperty] = useState<
    boolean
  >(false);
  const [openProcessDialog, setOpenProcessDialog] = useState<boolean>(false);
  const [isSearchNotEmpty, setIsSearchNotEmpty] = useState<boolean>(true);
  const notify = useNotifier();

  const [
    getDataSuggestions,
    { data: fetchDataSuggestions, loading: loadingDataSuggestion }
  ] = useSearchSuggestionMaterialLazyQuery({ fetchPolicy: "no-cache" });

  useEffect(() => {
    if (resetFilter) {
      handleResetForm();
    }
  }, [resetFilter]);

  useEffect(() => {
    const isFormNotEmpty =
      formik?.values?.soldTo &&
      formik?.values?.saleOrganization &&
      formik?.values?.distributionChannel &&
      (formik?.values?.soldToMaterialCode || formik?.values?.materialCode);
    setElementEnableProperty(!isFormNotEmpty);
  }, [
    formik?.values?.saleOrganization,
    formik?.values?.soldTo,
    formik?.values?.distributionChannel,
    formik?.values?.soldToMaterialCode,
    formik?.values?.materialCode
  ]);
  useEffect(() => {
    const isResetButtonEnabled =
      !!formik?.values?.soldTo ||
      !!formik?.values?.saleOrganization ||
      !!formik?.values?.distributionChannel ||
      !!formik?.values?.soldToMaterialCode ||
      !!formik?.values?.materialCode;
    setResetButtonEnableProperty(isResetButtonEnabled);
  }, [
    formik?.values?.saleOrganization,
    formik?.values?.soldTo,
    formik?.values?.distributionChannel,
    formik?.values?.soldToMaterialCode,
    formik?.values?.materialCode
  ]);

  useEffect(() => {
    if (!formik?.values?.soldTo) {
      handleResetForm();
    }
  }, [formik?.values?.soldTo]);

  const handleResetForm = () => {
    formik.resetForm();
    setSalesOrganizationOptions([]);
    setDistributionChannelOptions([]);
    setReset(prev => !prev);
    setDownloadDisable(false);
    onReset();
    setIsSearchNotEmpty(true);
  };

  const [
    getDataSoldTo,
    { data: fetchDataSoldTo, loading: loadingDataSoldTo }
  ] = useSearchSuggestionSoldTosCustMatLazyQuery();

  const dataSoldTo = useMemo(() => {
    const data =
      mapEdgesToItems(fetchDataSoldTo?.searchSuggestionSoldTosCustMat) || [];
    return data.map(data => ({
      title: data.domesticSoldToSearchFilterDisplayText,
      value: data.code
    }));
  }, [fetchDataSoldTo?.searchSuggestionSoldTosCustMat]);

  const handleChangeSoldToKeyword = async (value: string) => {
    if (value && value.length >= 2) {
      await getDataSoldTo({
        variables: { first: 100, filter: { search: value } }
      });
    }
  };

  const getValueFromOptionTitle = title =>
    dataSoldTo.find(x => x.title === title)?.value;

  const setDistributionChannelDefaultValue = (options: Choice[]) => {
    const defaultCode: string = "10"; // SEO-8354
    const defaultItem = options.find(option => option.value === defaultCode);
    if (defaultItem) {
      formik.setFieldValue("distributionChannel", defaultItem.value);
    }
  };

  const [getCustomerMaterials] = useCustomerMaterialsLazyQuery({
    fetchPolicy: "no-cache",
    onCompleted: data => {
      if (data && handleSearchData) {
        handleSearchData(data);
        setIsSearchNotEmpty(data.customerMaterials.length > 0);
      }
      setOpenProcessDialog(false);
    },
    onError: error => {
      setOpenProcessDialog(false);
      notify({
        status: "error",
        apiMessage: error.message
      });
    }
  });

  const {
    data: dataSaleOrganization
  } = useGetSaleOrgAndDistChannelBySoldToQuery({
    variables: {
      soldTo: formik?.values?.soldTo || ""
    },
    onCompleted: data => {
      const saleOrganizationChoices =
        data?.getSaleOrgAndDistChannelBySoldTo?.salesOrganization || [];
      const distributionChannels =
        data?.getSaleOrgAndDistChannelBySoldTo?.distributionChannel || [];

      if (saleOrganizationChoices.length > 0) {
        const salesOrganizationOptions: Choice[] = saleOrganizationChoices.map(
          item => ({
            value: item.code,
            label: item.displayText
          })
        );
        setSalesOrganizationOptions(salesOrganizationOptions);
      }
      if (distributionChannels.length > 0) {
        const distributionChannelOptions: Choice[] = distributionChannels.map(
          item => ({
            value: item.code,
            label: item.displayText
          })
        );
        setDistributionChannelOptions(distributionChannelOptions);
        setDistributionChannelDefaultValue(distributionChannelOptions);
      }
    }
  });

  const handleChangeSoldTo = (value: string) => {
    formik.setFieldValue("soldTo", getValueFromOptionTitle(value));
    formik.setFieldValue("saleOrganization", "");
    formik.setFieldValue("distributionChannel", "");
  };

  const handleChangeSaleOrganization = event => {
    const selectedValue = event.target.value;
    formik.setFieldValue("saleOrganization", selectedValue);

    if (selectedValue) {
      const distribution = dataSaleOrganization?.getSaleOrgAndDistChannelBySoldTo?.salesOrgByDistChannel.find(
        org => org.salesOrg === selectedValue
      );
      if (distribution) {
        const formattedData = distribution.distributionChannel.map(channel => ({
          value: channel.code,
          label: channel.displayText
        }));
        setDistributionChannelOptions(formattedData);
      } else {
        setDistributionChannelOptions([]);
      }
    } else {
      const distributionChannels =
        dataSaleOrganization?.getSaleOrgAndDistChannelBySoldTo
          ?.distributionChannel || [];
      const formattedData = distributionChannels.map(item => ({
        value: item.code,
        label: item.displayText
      }));
      setDistributionChannelOptions(formattedData);
    }
  };

  const handleChangeDistributionChannels = event => {
    const selectedValue = event.target.value;
    formik.setFieldValue("distributionChannel", selectedValue);

    if (selectedValue) {
      const salesOrgData = dataSaleOrganization?.getSaleOrgAndDistChannelBySoldTo?.distChannelBySalesOrg.find(
        org => org.distributionChannel === selectedValue
      );

      if (salesOrgData) {
        const formattedData = salesOrgData.salesOrg.map(channel => ({
          value: channel.code,
          label: channel.displayText
        }));
        setSalesOrganizationOptions(formattedData);
      } else {
        setSalesOrganizationOptions([]);
      }
    } else {
      const data = dataSaleOrganization?.getSaleOrgAndDistChannelBySoldTo?.salesOrganization?.map(
        item => ({
          value: item.code,
          label: item.displayText
        })
      );
      setSalesOrganizationOptions(data || []);
    }
  };

  const dataMatCode = useMemo(() => {
    const data =
      mapEdgesToItems(fetchDataSuggestions?.searchSuggestionMaterial) || [];
    return data.map(data => ({
      title: `${data.displayText}`,
      value: data.materialCode
    }));
  }, [fetchDataSuggestions?.searchSuggestionMaterial]);

  const getValueFromOptionMatCode = title =>
    dataMatCode.find(x => x.title === title)?.value;

  const handleChangeMatCodeKeyword = async (value: string) => {
    if (value && value.length >= 2) {
      await getDataSuggestions({
        variables: {
          first: 100,
          filter: { matCode: value },
          saleOrg: formik.values.saleOrganization || "",
          distributionChannel: formik.values.distributionChannel || "",
          searchBy: SearchMaterialBy.MAT_CODE,
          status: "all"
        }
      });
    }
  };

  const handleChangeMatCode = (value: string) => {
    formik.setFieldValue("materialCode", getValueFromOptionMatCode(value));
  };

  const handleChangeValue = e => {
    const { value } = e.target;
    formik.setFieldValue("soldToMaterialCode", value);
  };
  const onSubmitSearch = async () => {
    setOpenProcessDialog(true);
    if (formik.isValid) {
      formik.submitForm();
      try {
        await getCustomerMaterials({
          variables: {
            filter: {
              soldToCode: formik?.values?.soldTo || "",
              salesOrganizationCode: formik?.values?.saleOrganization || "",
              distributionChannelCode:
                formik?.values?.distributionChannel || "",
              materialCode: formik?.values?.materialCode || "",
              soldToMaterialCode: formik?.values?.soldToMaterialCode || ""
            }
          }
        });
      } catch (error) {
        console.error("Error fetching customer materials:", error);
        setOpenProcessDialog(false);
      }
    }
  };

  return (
    <Box className={classes.root}>
      <Grid className={classes.searchBox} container>
        <Grid container spacing={3} className={classes.negativeMargin}>
          <Grid item xs={12} className={classes.gridPadding}>
            <Grid item xs={6}>
              <div style={{ display: "flex" }}>
                <MyTypography variant={"subtitle3"}>Sold-to Party</MyTypography>
                <span className={classes.required}>*</span>
                <span>&nbsp;</span>
                <span> (เลือกได้เพียง 1 รายการ)</span>
              </div>
              <Autocomplete
                resetAlways
                optionsProp={dataSoldTo}
                placeholderProps={intl.formatMessage({
                  defaultMessage: "ค้นหา Sold-to Code/Name",
                  description: "label"
                })}
                onChangeProps={handleChangeSoldToKeyword}
                onSearch={handleChangeSoldTo}
                isReset={reset}
                loading={loadingDataSoldTo}
                id="soldTo"
                name="soldTo"
              />
            </Grid>
          </Grid>
          <Grid item xs={12} className={classes.boxContainer}>
            <Grid item xs={6}>
              <div style={{ display: "flex" }}>
                <MyTypography variant={"subtitle3"}>
                  Sales Organization
                </MyTypography>
                <span className={classes.required}>*</span>
              </div>
              <SingleSelectField
                menuListCustomStyle={{
                  overflowY: "auto"
                }}
                testId="saleOrganization"
                name={"saleOrganization"}
                id="saleOrganization"
                value={formik?.values?.saleOrganization}
                choices={salesOrganizationOptions || []}
                onChange={handleChangeSaleOrganization}
                placeholder={"เลือก Sales Organization"}
                className={classes.selectField}
              />
            </Grid>
            <Grid item xs={6}>
              <div style={{ display: "flex" }}>
                <MyTypography variant={"subtitle3"}>
                  Distribution Channel
                </MyTypography>
                <span className={classes.required}>*</span>
              </div>
              <SingleSelectField
                menuListCustomStyle={{
                  overflowY: "auto"
                }}
                testId="distributionChannel"
                name={"distributionChannel"}
                id="distributionChannel"
                value={formik?.values?.distributionChannel}
                onChange={handleChangeDistributionChannels}
                placeholder={"เลือก Distribution Channel"}
                choices={distributionChannelOptions || []}
                className={classes.selectField}
              />
            </Grid>
            <Grid item xs={6}>
              <div style={{ display: "flex" }}>
                <MyTypography variant={"subtitle3"}>
                  Customer Material
                </MyTypography>
                <span className={classes.required}>*</span>
              </div>
              <SingleTextField
                name={`custMatCodeSearch`}
                value={formik.values.soldToMaterialCode}
                maxLengthProps={35}
                showClearIcon={true}
                className={classes.inputTextField}
                onChange={event => {
                  handleChangeValue(event);
                }}
                placeholder={"ระบุ Customer Material"}
              />
            </Grid>
            <Grid item xs={6}>
              <div style={{ display: "flex" }}>
                <MyTypography variant={"subtitle3"}>
                  Material Code / Description
                </MyTypography>
                <span className={classes.required}>*</span>
              </div>
              <Autocomplete
                resetAlways
                optionsProp={dataMatCode}
                placeholderProps={intl.formatMessage({
                  defaultMessage: "ค้นหา Material Code / Description",
                  description: "label"
                })}
                onChangeProps={handleChangeMatCodeKeyword}
                onSearch={handleChangeMatCode}
                isReset={reset}
                loading={loadingDataSuggestion}
                id="materialCodeDescription"
                name="materialCodeDescription"
              />
            </Grid>
          </Grid>
          <Grid item xs={12} className={classes.warningBox}>
            <div className={classes.warningDiv}>
              <Typography className={classes.warningNote}>
                <FormattedMessage defaultMessage="โปรดระบุ " />
                <strong>Customer Material</strong>
                <FormattedMessage defaultMessage=" หรือ " />
                <strong>Customer Material</strong>
                <FormattedMessage defaultMessage=" อย่างน้อย 1 ช่อง หรือทั้ง 2 ช่อง ก่อนกดค้นหา " />
              </Typography>
            </div>
          </Grid>
          <Grid item xs={12} className={classes.boxContainer}>
            <div style={{ display: "flex" }}>
              {isSearchNotEmpty ? (
                <Typography className={classes.note}>
                  <InlineSVG
                    src={infoIcon}
                    style={{ marginTop: 0 }}
                    className={classes.messageIcon}
                  />
                  <FormattedMessage defaultMessage="กรุณากรอกข้อมูลที่จำเป็น (" />
                  <span className={classes.required}>*</span>
                  <FormattedMessage defaultMessage=") และกดค้นหา" />
                </Typography>
              ) : (
                <Typography className={classes.emptyMessage}>
                  <InlineSVG
                    src={warningRed}
                    style={{ marginTop: 0 }}
                    className={classes.messageIcon}
                  />
                  <FormattedMessage defaultMessage="ไม่พบข้อมูลที่ค้นหา" />
                </Typography>
              )}
            </div>
            <div className={classes.buttonContainer}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleResetForm}
                disabled={!resetButtonEnableProperty}
                className={classes.btn}
                id="reset"
                name="reset"
              >
                <InlineSVG src={RefreshIcon} width={24} height={24} />
                <MyTypography variant="body3" className={classes.btnTxt}>
                  <FormattedMessage defaultMessage="ล้างข้อมูล" />
                </MyTypography>
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={onSubmitSearch}
                disabled={elementEnableProperty}
                className={classes.btnSearch}
                id="reset"
                name="reset"
                style={{ maxWidth: "288px !important" }}
              >
                <InlineSVG src={Search} width={24} height={24} />
                <MyTypography variant="body3" className={classes.btnTxt}>
                  <FormattedMessage defaultMessage="Search Customer Material" />
                </MyTypography>
              </Button>
            </div>
          </Grid>
        </Grid>
      </Grid>
      {openProcessDialog && (
        <DialogWaitingConfirm
          open={openProcessDialog}
          onClose={() => setOpenProcessDialog(false)}
        />
      )}
    </Box>
  );
};

export default EditMaterial;
